import {
  Component,
  OnInit,
  signal,
  computed,
  inject,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';

import { combineLatest } from 'rxjs';

import { AuthStatus } from 'src/app/@enums/authentication/auth-status.enum';
import { AuthService } from 'src/app/@shared/authentication/auth.service';

import { HeaderComponent } from './components/header/header.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import {
  LayoutConfig,
  UserLayoutInfo,
  MenuItem,
  SidebarItem,
} from './models/layout.models';
import { MenuService } from './services/menu.service';

@Component({
  selector: 'app-main-layout',
  imports: [RouterOutlet, SidebarComponent, HeaderComponent],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly menuService = inject(MenuService);
  private readonly destroyRef = inject(DestroyRef);

  isSidebarCollapsed = signal(true);
  isAuthenticated = signal(false);
  userInfo = signal<UserLayoutInfo | null>(null);

  sidebarItems = computed(() => {
    const menuItems = this.menuService.getMenuItems()() as MenuItem[];
    return this.convertMenuItemsToSidebarItems(menuItems);
  });

  layoutConfig = signal<LayoutConfig>({
    showSidebar: true,
    showHeader: true,
    sidebarCollapsed: false,
  });

  ngOnInit(): void {
    this.setupAuthListener();
    this.authService.checkAuthStatus();
  }

  private setupAuthListener(): void {
    combineLatest([this.authService.authStatus$, this.authService.currentUser$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([status, user]) => {
        const isAuth = status === AuthStatus.authenticated;
        this.isAuthenticated.set(isAuth);

        if (isAuth && user) {
          const rol = user.roles?.[0];

          if (rol) {
            this.userInfo.set({
              name: user.name,
              roleName: rol.name,
            });

            this.menuService.setMenuItemsByRole(rol.Id_rol.toString());
          }
        } else {
          this.userInfo.set(null);
        }
      });
  }

  private convertMenuItemsToSidebarItems(menuItems: MenuItem[]): SidebarItem[] {
    return menuItems.map((item) => ({
      id: item.id,
      label: item.name,
      icon: item.icon,
      route: item.route,
      action: item.action,
      children: item.children
        ? this.convertMenuItemsToSidebarItems(item.children)
        : undefined,
    }));
  }

  onSidebarCollapsedChange(collapsed: boolean): void {
    this.isSidebarCollapsed.set(collapsed);
    this.layoutConfig.update((config) => ({
      ...config,
      sidebarCollapsed: collapsed,
    }));
  }

  onMobileMenuToggle(): void {
    const currentState = this.isSidebarCollapsed();
    this.onSidebarCollapsedChange(!currentState);
  }
}
